/**
 * Public documentation site (/docs and /docs/:sectionId).
 *
 * Renders the same MDX sections as the in-app DocsPanel, but presented as a
 * proper documentation site: sticky section nav, long-form article column,
 * "on this page" outline, and prev/next reading flow. The compact boxed
 * layout stays in-app; this page is built for reading and learning.
 */

import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MDXProvider } from '@mdx-js/react';
import PageLayout from './PageLayout';
import { CATEGORIES, SECTIONS, DEFAULT_SECTION, visibleCategories } from '../docs/registry';
import { makeMdxComponents } from '../docs/mdxComponents';

const siteComponents = makeMdxComponents('site');

/** Flat ordered list of visible sections, for prev/next navigation. */
function flatSections() {
  return visibleCategories(null).flatMap(cat =>
    cat.items.filter(item => SECTIONS[item.id]).map(item => ({ ...item, category: cat.title })));
}

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/** Scans the rendered article for h2 headings and renders an outline. */
function OnThisPage({ articleRef, sectionId }) {
  const [headings, setHeadings] = useState([]);

  useEffect(() => {
    setHeadings([]);
    let attempts = 0;
    const scan = () => {
      const el = articleRef.current;
      const h2s = el ? Array.from(el.querySelectorAll('h2')) : [];
      if (h2s.length > 0) {
        setHeadings(h2s.map(h => {
          if (!h.id) h.id = slugify(h.textContent);
          return { id: h.id, text: h.textContent };
        }));
      } else if (attempts++ < 20) {
        setTimeout(scan, 150); // lazy MDX chunk may still be loading
      }
    };
    scan();
    return () => { attempts = 99; };
  }, [sectionId, articleRef]);

  if (headings.length < 2) return null;
  return (
    <nav style={{ fontSize: '13px' }}>
      <div style={{
        color: '#64748b', fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', fontSize: '11px', marginBottom: '12px',
      }}>
        On this page
      </div>
      {headings.map(h => (
        <a
          key={h.id}
          href={`#${h.id}`}
          style={{
            display: 'block', color: '#7c8a9e', textDecoration: 'none',
            padding: '4px 0', lineHeight: 1.45, transition: 'color 0.15s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#e2e8f0'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#7c8a9e'}
        >
          {h.text}
        </a>
      ))}
    </nav>
  );
}

export default function DocsPage() {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const activeSection = sectionId && (sectionId in SECTIONS) ? sectionId : DEFAULT_SECTION;
  const articleRef = useRef(null);

  const categories = visibleCategories(null);
  const sections = flatSections();
  const idx = sections.findIndex(s => s.id === activeSection);
  const current = sections[idx];
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx >= 0 && idx < sections.length - 1 ? sections[idx + 1] : null;

  // Scroll to top on section change
  useEffect(() => { window.scrollTo(0, 0); }, [activeSection]);

  const SectionComponent = SECTIONS[activeSection];

  return (
    <PageLayout>
      <style>{`
        @media (max-width: 1280px) { .docs-toc { display: none; } }
        @media (max-width: 900px) { .docs-sidebar { display: none; } }
      `}</style>
      <div style={{
        display: 'flex',
        maxWidth: '1500px',
        margin: '0 auto',
        padding: '0 2rem',
        gap: '3rem',
        alignItems: 'flex-start',
      }}>
        {/* Section nav */}
        <aside className="docs-sidebar" style={{
          width: '220px',
          flexShrink: 0,
          position: 'sticky',
          top: '24px',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          padding: '8px 0 32px',
        }}>
          {categories.map(cat => (
            <div key={cat.title} style={{ marginBottom: '24px' }}>
              <div style={{
                color: '#64748b', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', fontSize: '11px', marginBottom: '8px',
              }}>
                {cat.title}
              </div>
              {cat.items.map(item => {
                const isActive = item.id === activeSection;
                const hasContent = !!SECTIONS[item.id];
                if (!hasContent) return null;
                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(`/docs/${item.id}`)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '5px 10px',
                      marginLeft: '-10px',
                      border: 'none',
                      borderRadius: '6px',
                      background: isActive ? 'rgba(59,130,246,0.1)' : 'transparent',
                      color: isActive ? '#60a5fa' : '#94a3b8',
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '13.5px',
                      lineHeight: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = '#e2e8f0'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = '#94a3b8'; }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        {/* Article */}
        <main style={{ flex: 1, minWidth: 0, maxWidth: '820px', paddingBottom: '4rem' }}>
          {current && (
            <div style={{
              color: '#3b82f6', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.08em', fontSize: '12px', marginBottom: '10px',
            }}>
              {current.category}
            </div>
          )}
          <article ref={articleRef}>
            <MDXProvider components={siteComponents}>
              <Suspense fallback={
                <div style={{ color: '#475569', padding: '60px 0', fontSize: '14px' }}>Loading…</div>
              }>
                {SectionComponent && <SectionComponent />}
              </Suspense>
            </MDXProvider>
          </article>

          {/* Prev / Next */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: '1rem',
            marginTop: '56px', paddingTop: '24px', borderTop: '1px solid #1e293b',
          }}>
            {prev ? (
              <Link to={`/docs/${prev.id}`} style={{
                textDecoration: 'none', color: '#94a3b8', fontSize: '14px',
                padding: '12px 16px', borderRadius: '8px', border: '1px solid #1e293b',
                flex: '0 1 48%',
              }}>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>← Previous</div>
                <div style={{ color: '#cbd5e1', fontWeight: 500 }}>{prev.label}</div>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/docs/${next.id}`} style={{
                textDecoration: 'none', color: '#94a3b8', fontSize: '14px',
                padding: '12px 16px', borderRadius: '8px', border: '1px solid #1e293b',
                flex: '0 1 48%', textAlign: 'right',
              }}>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Next →</div>
                <div style={{ color: '#cbd5e1', fontWeight: 500 }}>{next.label}</div>
              </Link>
            ) : <span />}
          </div>
        </main>

        {/* On this page */}
        <aside className="docs-toc" style={{
          width: '200px',
          flexShrink: 0,
          position: 'sticky',
          top: '24px',
        }}>
          <OnThisPage articleRef={articleRef} sectionId={activeSection} />
        </aside>
      </div>
    </PageLayout>
  );
}
